/* Native dialog provides focus containment, inert background and Escape handling.
   The details menu remains usable if JS or showModal is unavailable. */
if (!customElements.get('site-header')) {
  customElements.define('site-header', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.toggle = this.querySelector('[data-menu-toggle]');
      this.dialog = this.querySelector('[data-menu-dialog]');
      this.fallback = this.querySelector('[data-menu-fallback]');
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', this.onScroll, { ...options, passive: true });
      this.watchHeroLogo();
      this.onScroll();
      window.addEventListener('resize', () => this.watchHeroLogo(), options);
      // Theme editor: the hero can be added, removed or re-rendered without a page load.
      for (const type of ['shopify:section:load', 'shopify:section:unload']) {
        document.addEventListener(type, () => requestAnimationFrame(() => this.watchHeroLogo()), options);
      }
      this.setupSearch(options);
      if (!this.toggle || !this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.toggle.hidden = false;
      this.fallback.hidden = true;
      this.toggle.addEventListener('click', () => this.openMenu(), options);
      this.querySelector('[data-menu-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => this.resetMenu(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) this.dialog.close();
      }, options);
      this.addEventListener('shopify:section:select', () => this.dialog.close(), options);
    }

    setupSearch(options) {
      this.searchTrigger = this.querySelector('.header__search');
      this.searchPanel = this.querySelector('[data-search-panel]');
      if (!this.searchTrigger || !this.searchPanel) return;
      this.searchInput = this.searchPanel.querySelector('[role="combobox"]');
      this.searchList = this.searchPanel.querySelector('[data-search-results]');
      this.searchStatus = this.searchPanel.querySelector('[data-search-status]');
      this.searchTrigger.setAttribute('aria-controls', this.searchPanel.id);
      this.searchTrigger.setAttribute('aria-expanded', 'false');
      this.searchTrigger.addEventListener('click', (event) => {
        // Keep modified clicks usable as the original search-page link.
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (!this.searchPanel.hidden) return this.closeSearch();
        this.searchPanel.hidden = false;
        this.classList.add('search-open');
        this.searchTrigger.setAttribute('aria-expanded', 'true');
        this.searchInput.focus({ preventScroll: true });
        this.queueSearch();
      }, options);
      this.searchPanel.querySelector('[data-search-close]').addEventListener('click', () => this.closeSearch(), options);
      document.addEventListener('click', (event) => {
        if (!this.searchPanel.hidden && !this.searchPanel.contains(event.target) && !this.searchTrigger.contains(event.target)) this.closeSearch();
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.searchPanel.hidden) {
          event.preventDefault();
          this.closeSearch();
        }
      }, options);
      this.searchInput.addEventListener('compositionstart', () => {
        this.cancelSearch();
        this.clearSearch();
      }, options);
      this.searchInput.addEventListener('input', (event) => {
        if (!event.isComposing) this.queueSearch();
      }, options);
      this.searchInput.addEventListener('compositionend', () => this.queueSearch(), options);
      this.searchInput.addEventListener('keydown', (event) => {
        if (event.isComposing || event.keyCode === 229) return;
        const items = [...this.searchList.querySelectorAll('[role="option"]')];
        if (['ArrowDown', 'ArrowUp'].includes(event.key) && items.length) {
          event.preventDefault();
          const next = event.key === 'ArrowDown' ? this.searchIndex + 1 : this.searchIndex < 0 ? items.length - 1 : this.searchIndex - 1;
          this.searchIndex = (next + items.length) % items.length;
          items.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.searchIndex)));
          this.searchInput.setAttribute('aria-activedescendant', items[this.searchIndex].id);
          items[this.searchIndex].scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'Enter' && items[this.searchIndex]) {
          event.preventDefault();
          items[this.searchIndex].click();
        }
      }, options);
      // Options keep DOM focus on the combobox, including when selected with a pointer.
      this.searchList.addEventListener('mousedown', (event) => event.preventDefault(), options);
      this.searchPanel.querySelector('form').addEventListener('submit', () => this.cancelSearch(), options);
      this.clearSearch();
    }

    cancelSearch() {
      clearTimeout(this.searchTimer);
      this.searchRequest?.abort();
      this.searchSequence = (this.searchSequence || 0) + 1;
    }

    clearSearch() {
      this.searchIndex = -1;
      this.searchList.replaceChildren();
      this.searchList.hidden = true;
      this.searchInput.setAttribute('aria-expanded', 'false');
      this.searchInput.removeAttribute('aria-activedescendant');
      this.searchStatus.textContent = '';
    }

    closeSearch() {
      if (!this.searchPanel || this.searchPanel.hidden) return;
      this.cancelSearch();
      this.clearSearch();
      this.searchPanel.hidden = true;
      this.classList.remove('search-open');
      this.searchTrigger.setAttribute('aria-expanded', 'false');
      if (this.isConnected) this.searchTrigger.focus({ preventScroll: true });
    }

    queueSearch() {
      this.cancelSearch();
      this.clearSearch();
      const query = this.searchInput.value.trim();
      if (!query || this.searchPanel.hidden) return;
      const sequence = this.searchSequence;
      this.searchTimer = setTimeout(() => this.fetchSearch(query, sequence), 250);
    }

    async fetchSearch(query, sequence) {
      this.searchRequest = new AbortController();
      this.searchStatus.textContent = this.searchPanel.dataset.loading;
      try {
        const url = new URL(this.searchPanel.dataset.predictiveUrl + '.json', window.location.origin);
        url.search = new URLSearchParams({ q: query, 'resources[type]': 'product,query,collection,article,page', 'resources[limit]': '4' });
        const response = await fetch(url, { signal: this.searchRequest.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
        const data = await response.json();
        if (sequence !== this.searchSequence) return;
        const results = data.resources?.results;
        if (!results || typeof results !== 'object') throw new Error('Invalid predictive search response');
        for (const type of ['products', 'queries', 'collections', 'articles', 'pages']) {
          for (const result of (Array.isArray(results[type]) ? results[type] : []).slice(0, 4)) {
            const label = type === 'queries' ? result.text : result.title;
            if (typeof label !== 'string' || !label.trim() || typeof result.url !== 'string') continue;
            const target = new URL(result.url, window.location.origin);
            if (target.origin !== window.location.origin || !['http:', 'https:'].includes(target.protocol)) continue;
            const item = document.createElement('li');
            item.setAttribute('role', 'none');
            const link = document.createElement('a');
            link.id = `${this.searchList.id}-${this.searchList.children.length}`;
            link.href = target.href;
            link.textContent = label;
            link.setAttribute('role', 'option');
            link.setAttribute('aria-selected', 'false');
            link.tabIndex = -1;
            item.append(link);
            this.searchList.append(item);
          }
        }
        const found = this.searchList.children.length > 0;
        this.searchList.hidden = !found;
        this.searchInput.setAttribute('aria-expanded', String(found));
        this.searchStatus.textContent = this.searchPanel.dataset[found ? 'results' : 'empty'];
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.searchSequence) return;
        this.clearSearch();
        this.searchStatus.textContent = this.searchPanel.dataset.error;
      }
    }

    // Home: hide the nav wordmark while the hero wordmark is in view below the sticky header (owner, 2026-10-13).
    // Without the hero wordmark or IntersectionObserver the nav wordmark simply stays.
    watchHeroLogo() {
      const heroLogo = typeof IntersectionObserver === 'function' ? document.querySelector('[data-section="home-hero"] .hero__logo') : null;
      // Stay hidden through the geometry reads below, or the logo flashes in and out right after definition.
      if (heroLogo && !this.dataset.logo) this.dataset.logo = 'hero';
      const bar = this.closest('.site-header-section') || this;
      const height = Math.round(bar.getBoundingClientRect().height);
      if (heroLogo === this.heroLogo && height === this.barHeight) return;
      this.heroObserver?.disconnect();
      this.heroObserver = null;
      this.heroLogo = heroLogo;
      this.barHeight = height;
      if (!heroLogo) {
        delete this.dataset.logo;
        return;
      }
      // Decide before the first paint after definition; the observer keeps it right from here on.
      const box = heroLogo.getBoundingClientRect();
      this.dataset.logo = box.bottom > height && box.top < window.innerHeight ? 'hero' : 'header';
      try {
        this.heroObserver = new IntersectionObserver((entries) => {
          this.dataset.logo = entries.at(-1).isIntersecting ? 'hero' : 'header';
        }, { root: document, rootMargin: `-${height}px 0px 0px 0px` });
        this.heroObserver.observe(heroLogo);
      } catch {
        delete this.dataset.logo;
      }
    }

    openMenu() {
      if (this.dialog.open) return;
      this.closeSearch();
      this.dialog.showModal();
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('menu-open');
      this.dialog.querySelector('[data-menu-close]').focus({ preventScroll: true });
    }

    resetMenu() {
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
      if (this.isConnected) this.toggle.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.closeSearch();
      this.cancelSearch();
      this.heroObserver?.disconnect();
      this.heroObserver = this.heroLogo = null;
      if (this.dialog?.open) this.dialog.close();
      if (this.toggle) this.resetMenu();
    }
  });
}
